import React, { useState, useEffect, useCallback } from 'react';
import { posService, billingService } from '../../services/dataService';
import { tzMidnightUTC, todayInTz, getHourInTz } from '../../utils/tzUtils';

const fmt = (n) => `₹${(n || 0).toFixed(2)}`;
const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const KPICard = ({ label, value, sub, color = '#FF4444' }) => (
  <div style={{
    background: 'white', borderRadius: '16px', padding: '22px 24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.06)',
    flex: '1 1 180px',
  }}>
    <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>{label}</div>
    <div style={{ fontSize: '1.8rem', fontWeight: '800', color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '6px' }}>{sub}</div>}
  </div>
);

const PaymentBar = ({ label, amount, total, color }) => {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#555' }}>{label}</span>
        <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#333' }}>{fmt(amount)} <span style={{ color: '#aaa', fontWeight: '400' }}>({pct}%)</span></span>
      </div>
      <div style={{ height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
};

const ManagerDashboard = ({ restaurant, managerId, managerName }) => {
  const [orders, setOrders] = useState([]);
  const [billingSettings, setBillingSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const tz = restaurant?.timezone || 'Asia/Kolkata';
  const today = tzMidnightUTC(todayInTz(tz), tz);

  const loadData = useCallback(async () => {
    if (!restaurant?.id) return;
    const [ordersRes, settingsRes] = await Promise.all([
      posService.getOrdersForReport(
        restaurant.id,
        today.toISOString(),
        new Date().toISOString(),
        managerId   // filters to this manager's orders only
      ),
      billingService.getSettings(restaurant.id),
    ]);
    setOrders(ordersRes.data || []);
    setBillingSettings(settingsRes.data);
    setLoading(false);
  }, [restaurant?.id, managerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // Poll every 30s for fresh data
  useEffect(() => {
    const iv = setInterval(() => loadData(), 30000);
    return () => clearInterval(iv);
  }, [loadData]);

  // ── Derived metrics ───────────────────────────────────────────────────────
  const myInvoices   = orders.filter(o => o.invoice_number && !o.is_invalid);
  const paidInvoices = myInvoices.filter(o => o.paid_at);
  const unpaidInvoices = myInvoices.filter(o => !o.paid_at);

  const totalBilled    = myInvoices.reduce((s, o) => s + (o.total_payable || 0), 0);
  const totalCollected = paidInvoices.reduce((s, o) => s + (o.total_payable || 0), 0);
  const totalPending   = unpaidInvoices.reduce((s, o) => s + (o.total_payable || 0), 0);

  const pmBreakdown = { cash: 0, card: 0, upi: 0, other: 0 };
  paidInvoices.forEach(o => {
    const pm = (o.payment_method || 'other').toLowerCase();
    if (pm in pmBreakdown) pmBreakdown[pm] += o.total_payable || 0;
    else pmBreakdown.other += o.total_payable || 0;
  });

  // Hourly order count (for today) — in restaurant timezone
  const hourly = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }));
  orders.forEach(o => {
    const h = getHourInTz(o.created_at, tz);
    hourly[h].count++;
  });
  const activeHours = hourly.filter(h => h.count > 0);
  const peakHour = activeHours.length > 0
    ? activeHours.reduce((max, h) => h.count > max.count ? h : max, activeHours[0])
    : null;

  const formatHour = (h) => h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;
  const maxCount = Math.max(...hourly.map(h => h.count), 1);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #6366f1', borderTop: '4px solid transparent', borderRadius: '50%', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
          <h2 style={{ color: '#333', margin: 0 }}>Loading Dashboard...</h2>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: '1.8rem', fontWeight: '700', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          My Shift Today
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })} · {managerName}
        </p>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '28px' }}>
        <KPICard label="Invoices Generated" value={myInvoices.length} sub="By you today" color="#6366f1" />
        <KPICard label="Total Billed" value={fmt(totalBilled)} sub="All invoices today" color="#FF4444" />
        <KPICard label="Collected" value={fmt(totalCollected)} sub={`${paidInvoices.length} paid`} color="#16a34a" />
        <KPICard label="Pending" value={fmt(totalPending)} sub={`${unpaidInvoices.length} unpaid`} color={totalPending > 0 ? '#f59e0b' : '#aaa'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>

        {/* Payment breakdown */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '1rem', fontWeight: '700', color: '#333' }}>Payment Collection</h2>
          {totalCollected === 0 ? (
            <p style={{ color: '#aaa', fontSize: '0.9rem', margin: 0 }}>No payments collected yet today.</p>
          ) : (
            <>
              <PaymentBar label="Cash" amount={pmBreakdown.cash} total={totalCollected} color="#16a34a" />
              <PaymentBar label="Card" amount={pmBreakdown.card} total={totalCollected} color="#6366f1" />
              <PaymentBar label="UPI" amount={pmBreakdown.upi} total={totalCollected} color="#f59e0b" />
              <PaymentBar label="Other" amount={pmBreakdown.other} total={totalCollected} color="#9ca3af" />
              <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '14px', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.9rem' }}>
                <span>Total Collected</span><span>{fmt(totalCollected)}</span>
              </div>
            </>
          )}
        </div>

        {/* Hourly activity */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: '700', color: '#333' }}>Hourly Activity</h2>
          <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: '#aaa' }}>
            {peakHour ? `Peak: ${formatHour(peakHour.hour)} (${peakHour.count} orders)` : 'No orders yet today'}
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '80px' }}>
            {hourly.filter((_, i) => i >= 6 && i <= 23).map(h => (
              <div key={h.hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  width: '100%', borderRadius: '3px 3px 0 0',
                  background: h.count > 0 ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : '#f3f4f6',
                  height: `${Math.max((h.count / maxCount) * 64, h.count > 0 ? 6 : 2)}px`,
                  transition: 'height 0.4s ease',
                }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.7rem', color: '#aaa' }}>
            <span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
          </div>
        </div>
      </div>

      {/* Unpaid invoices */}
      {unpaidInvoices.length > 0 && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #fcd34d', marginBottom: '28px' }}>
          <h2 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: '700', color: '#92400e' }}>
            ⚠️ Unpaid Invoices ({unpaidInvoices.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {unpaidInvoices.slice(0, 8).map(o => (
              <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a' }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#333' }}>{o.invoice_number}</div>
                  <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '2px' }}>{fmtDateTime(o.billed_at)}</div>
                </div>
                <div style={{ fontWeight: '800', fontSize: '1rem', color: '#f59e0b' }}>{fmt(o.total_payable)}</div>
              </div>
            ))}
            {unpaidInvoices.length > 8 && (
              <p style={{ margin: 0, color: '#888', fontSize: '0.85rem', textAlign: 'center' }}>
                +{unpaidInvoices.length - 8} more — go to Invoices tab to view all.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Recent invoices */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.06)' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: '700', color: '#333' }}>Recent Invoices</h2>
        {myInvoices.length === 0 ? (
          <p style={{ color: '#aaa', fontSize: '0.9rem', margin: 0 }}>No invoices generated yet today.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {myInvoices.slice(0, 10).map(o => (
              <div key={o.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', borderRadius: '10px',
                background: o.paid_at ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${o.paid_at ? '#bbf7d0' : '#fde68a'}`,
              }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '0.88rem', color: '#333' }}>{o.invoice_number}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>{fmtDateTime(o.billed_at)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: '700', fontSize: '0.9rem' }}>{fmt(o.total_payable)}</span>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px',
                    background: o.paid_at ? '#dcfce7' : '#fef3c7',
                    color: o.paid_at ? '#16a34a' : '#92400e',
                  }}>
                    {o.paid_at ? (o.payment_method || 'PAID').toUpperCase() : 'UNPAID'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDashboard;
